import { Page } from "modules/Page";
import { JobListing, JobListingsApi, jobListingsApi, JobListingsQueryParams, ValueCount } from "api/JobListingsApi";
import { Select } from "components/Select/Select";
import { debounce } from "modules/debounce";

import jobListingsTemplate from "./JobListings.hbs";
import mobileFilterTemplate from "../../components/Filters/FilterMobile.hbs";

import "./job-listings-page.scss";

enum SelectFilterType {
    Locations = "Locations",
    Categories = "Categories",
    Positions = "Positions",
    Countries = "Countries",
    Regions = "Regions",
    Experience = "Experience",
}

enum FilterType {
    Radius = "radius",
    StringQuery = "q",
    Page = "page",
    Language = "lang"
}

type FilterState = {
    selectedValues: {
        [key in SelectFilterType]: string[];
    };
    [FilterType.Radius]: number;
    [FilterType.StringQuery]: string;
    [FilterType.Page]: number;
    [FilterType.Language]?: string;
};

type State = {
    jobListings: JobListing[];
    filters: FilterState;
    totalJobListings: number;
    totalPages: number;
};

export class JobListingsPage extends Page<typeof JobListingsPage, "showExperienceLevel" | "hidePositionInResults"> {
    static readonly className = "js-job-listings-page";

    private searchParams: URLSearchParams;
    private showExperienceLevel: boolean;
    private hidePositionInResults: boolean;

    private openPositionsCountElement: HTMLElement;
    private jobListElement: HTMLElement;
    private loadMoreButton: HTMLButtonElement | null;
    private radiusInputElement: HTMLInputElement | null = null;
    private searchInputElement: HTMLInputElement | null = null;
    private filtersContainer: HTMLElement | null = null;

    private selects: { [key in SelectFilterType]: Select | null } = {
        [SelectFilterType.Locations]: null,
        [SelectFilterType.Categories]: null,
        [SelectFilterType.Positions]: null,
        [SelectFilterType.Countries]: null,
        [SelectFilterType.Regions]: null,
        [SelectFilterType.Experience]: null
    };


    private state: State = {
        jobListings: [] as JobListing[],
        filters: {
            selectedValues: {
                [SelectFilterType.Locations]: [],
                [SelectFilterType.Categories]: [],
                [SelectFilterType.Positions]: [],
                [SelectFilterType.Countries]: [],
                [SelectFilterType.Regions]: [],
                [SelectFilterType.Experience]: []
            },
            [FilterType.Radius]: 0,
            [FilterType.StringQuery]: "",
            [FilterType.Page]: 1,
        },
        totalJobListings: 0,
        totalPages: 0
    }

    constructor(
        rootElement: HTMLElement,
        index: number,
        private readonly api: JobListingsApi = jobListingsApi,
    ) {
        super(rootElement, index);

        this.showExperienceLevel = this.dataset.showExperienceLevel === "True";
        this.hidePositionInResults = this.dataset.hidePositionInResults === "True";
        this.searchParams = new URLSearchParams(window.location.search);

        const lang = document.getElementsByTagName("html")[0].getAttribute("lang");

        if (lang) {
            this.state.filters[FilterType.Language] = lang;
        }

        this.openPositionsCountElement = rootElement.querySelector<HTMLElement>(".open-positions > .count")!;
        this.jobListElement = rootElement.querySelector<HTMLElement>(".job-list")!;
        this.loadMoreButton = rootElement.querySelector<HTMLButtonElement>(".load-more")!;
        this.radiusInputElement = rootElement.querySelector<HTMLInputElement>("#radius");
        this.filtersContainer = rootElement.querySelector(".filters") as HTMLElement;
        this.searchInputElement = this.rootElement.querySelector<HTMLInputElement>(".filter-search input[type='search']");

        Object.values(SelectFilterType).forEach((selectFilterType) => {
            const select = this.getComponent<Select>(`[name='${selectFilterType}']`);
            this.selects[selectFilterType] = select;
            select?.on("change", this.handleSelectChange(selectFilterType));

            const selectFilterSearchParam = this.searchParams.get(selectFilterType);

            if (!selectFilterSearchParam?.length) return;

            const selectFilterSearchParamArray = selectFilterSearchParam.split(",");
            this.state.filters.selectedValues[selectFilterType] = selectFilterSearchParamArray;
            select?.setSelectedValues(selectFilterSearchParamArray);
        });

        this.loadMoreButton?.addEventListener("click", () => {
            this.loadNextPage();
        });

        this.initRadiusFilter();
        this.initFilterGroup();
        this.initSearchFilter();

        this.refreshJobListings();
    }

    private handleSelectChange = (filterType: SelectFilterType) => {
        return (selectedValues: string[]) => {
            this.state.filters.selectedValues[filterType] = selectedValues;

            // Enable/disable the radius filter based on if there are selected values in the locations filter
            if (filterType == SelectFilterType.Locations) {
                const radiusFilterContentElement = this.radiusInputElement?.closest(".filter-content");

                if (selectedValues.length) {
                    this.radiusInputElement?.removeAttribute("disabled");
                    radiusFilterContentElement?.classList.add("active");
                } else {
                    this.radiusInputElement?.setAttribute("disabled", "");
                    radiusFilterContentElement?.classList.remove("active");
                    this.resetRadiusFilter();
                }
            }

            this.refreshJobListings();
        }
    }

    initFilterGroup = () => {
        const mainElement = document.querySelector("main");

        const mobileButtonContainer = document.createElement("div");
        const mobileButtonLabel = this.filtersContainer?.dataset.labelFilterMobile || "Filter";

        mobileButtonContainer.classList.add("filter-mobile");
        mobileButtonContainer.style.opacity = '0';
        mobileButtonContainer.id = "filter-mobile";
        mobileButtonContainer.innerHTML = mobileFilterTemplate({ "label": mobileButtonLabel });
        mainElement?.insertAdjacentElement("beforeend", mobileButtonContainer);

        window.addEventListener("scroll", () => {
            mobileButtonContainer.style.opacity = window.scrollY > 10 ? "1" : "0";
        });

        const filtersContainer = document.querySelector(".filters");

        const mobileFilterButton = document.getElementById("filter-mobile");
        const mobileMinimizeFilters = document.getElementById("filters-collapse-mobile");

        mobileFilterButton?.addEventListener("click", () => {
            filtersContainer?.classList.add("active");
            mainElement?.classList.add("disabled");
        });

        mobileMinimizeFilters?.addEventListener("click", () => {
            filtersContainer?.classList.remove("active");
            mainElement?.classList.remove("disabled");
        });

        mainElement?.addEventListener("click", (e: Event) => {
            if (e.target != mainElement) return;

            if (mainElement.classList.contains("disabled")) {
                mainElement.classList.remove("disabled");
                filtersContainer?.classList.remove("active");
            }
        });

        const openPositionsElement = this.rootElement.querySelector(".open-positions");

        if (mobileFilterButton && openPositionsElement) {
            openPositionsElement.insertAdjacentElement("beforeend", mobileFilterButton);
        }

        const filterClearAll = document.getElementById("filter-clear");
        const mobileClearAllFilters = document.getElementById("filter-clear-mobile");

        filterClearAll?.addEventListener("click", this.handleClearAllFilters);
        mobileClearAllFilters?.addEventListener("click", this.handleClearAllFilters);
    }

    handleClearAllFilters = () => {
        for (const select in this.selects) {
            const selectComponent = this.selects[select as SelectFilterType];
            selectComponent?.clearOptions();
            selectComponent?.resetLabels();
        }

        this.resetRadiusFilter();
        this.resetSearchFilter();

        const radiusFilterContainer = this.rootElement.querySelector<HTMLDivElement>(".filter-custom");
        radiusFilterContainer?.classList.remove("active");
    }

    initSearchFilter = () => {
        const stringQuery = this.searchParams?.get(FilterType.StringQuery);

        if (stringQuery && this.searchInputElement) {
            this.state.filters[FilterType.StringQuery] = stringQuery;
            this.searchInputElement.value = stringQuery;
        }

        const clearSearchButton = this.rootElement.querySelector<HTMLButtonElement>(".filter-search button.clear__button");

        if (!clearSearchButton) return;

        if (this.state.filters[FilterType.StringQuery]?.length > 0) {
            clearSearchButton.style.display = "flex";
        }

        this.searchInputElement?.addEventListener("keyup", () => {
            this.state.filters[FilterType.StringQuery] = this.searchInputElement?.value || "";
            this.refreshJobListings();

            if (this.searchInputElement?.value) {
                clearSearchButton.style.display = "flex";
            }
            else {
                clearSearchButton.style.display = "none";
            }
        });

        clearSearchButton?.addEventListener("click", () => {
            this.state.filters[FilterType.StringQuery] = "";
            clearSearchButton.style.display = "none";

            if (this.searchInputElement) {
                this.searchInputElement.value = "";
            }
    
            this.refreshJobListings();
        });
    }

    private resetRadiusFilter() {
        if (this.radiusInputElement) {
            this.radiusInputElement.value = "0";
            const radiusValueElement = this.rootElement.querySelector("#radius-value") as HTMLSpanElement;
            radiusValueElement.innerHTML = "0 km";
            this.state.filters[FilterType.Radius] = 0;
        }
    }

    private resetSearchFilter() {
        if (this.searchInputElement) {
            this.searchInputElement.value = "";
            this.state.filters[FilterType.StringQuery] = "";
        }
    }

    private getJobListingsHtml(jobListings: JobListing[]) {
        return jobListingsTemplate({ 
            jobListings, 
            showExperienceLevel: this.showExperienceLevel, 
            hidePositionInResults: this.hidePositionInResults 
        });
    }

    private refreshJobListings = debounce(async () => {
        this.state.filters[FilterType.Page] = 1;

        const params = this.getRequestParams();
        const jobListingsPageData = await this.api.getJobListings(params);

        window.history.replaceState({}, "", `${window.location.pathname}?${new URLSearchParams(params as any)}`);

        this.state.totalJobListings = jobListingsPageData.totalJobListings;
        this.state.totalPages = jobListingsPageData.totalPages;

        this.updateSelectOptions(SelectFilterType.Locations, jobListingsPageData.relevantLocations);
        this.updateSelectOptions(SelectFilterType.Categories, jobListingsPageData.relevantCategories);
        this.updateSelectOptions(SelectFilterType.Positions, jobListingsPageData.relevantPositions);
        this.updateSelectOptions(SelectFilterType.Experience, jobListingsPageData.relevantExperience);

        if (this.loadMoreButton) {
            if (this.state.totalPages > 1) {
                this.loadMoreButton.style.display = "block";
            } else {
                this.loadMoreButton.style.display = "none";
            }
        }

        this.openPositionsCountElement.textContent = jobListingsPageData.totalJobListings.toString();
        this.state.jobListings = jobListingsPageData.jobListings;
        this.jobListElement.innerHTML = this.getJobListingsHtml(this.state.jobListings);
    }, 500);

    private updateSelectOptions = (filterType: SelectFilterType, relevantValues: ValueCount[]) => {
        const select = this.selects[filterType];
        select?.disableAllOptions();
        select?.resetLabels();

        relevantValues.forEach((valueCount) => {
            select?.enableOption(valueCount.value);
            const currentLabel = select?.getLabel(valueCount.value);
            select?.updateLabel(valueCount.value, `${currentLabel} (${valueCount.count})`);
        });
    };

    private loadNextPage = debounce(async () => {
        this.state.filters[FilterType.Page] += 1;

        if (this.loadMoreButton && this.state.filters[FilterType.Page] == this.state.totalPages) {
            this.loadMoreButton.style.display = "none";
        }

        const params = this.getRequestParams();
        const jobListingsPageData = await this.api.getJobListings(params);

        this.state.jobListings.push(...jobListingsPageData.jobListings);
        this.jobListElement.innerHTML += this.getJobListingsHtml(jobListingsPageData.jobListings);
    });

    private getRequestParams() {
        const params: JobListingsQueryParams = {};

        Object.values(SelectFilterType).forEach((selectFilterType) => {
            const selectedValues = this.state.filters.selectedValues[selectFilterType];
            if (selectedValues.length) {
                params[selectFilterType] = selectedValues.join(",");
            }
        });

        params[FilterType.Radius] = this.state.filters[FilterType.Radius];
        params[FilterType.StringQuery] = this.state.filters[FilterType.StringQuery];
        params[FilterType.Page] = this.state.filters[FilterType.Page];

        // Add the language parameter
        if (this.state.filters[FilterType.Language]) {
            params.lang = this.state.filters[FilterType.Language];
        }

        return params;
    }

    public initRadiusFilter() {
        const dl = this.radiusInputElement?.closest("dl");
        const radiusFilterValueElement = this.rootElement.querySelector<HTMLElement>("#radius-value");
        const radiusFilterContainer = this.rootElement.querySelector<HTMLElement>(".filter-custom");
        const radiusFilterHeader = this.rootElement.querySelector<HTMLButtonElement>(".filter-custom .filter-header");
        const radiusFilterContent = this.rootElement.querySelector<HTMLElement>(".filter-custom .filter-content");

        if (!this.radiusInputElement || !radiusFilterValueElement || !dl) return;

        const radius = this.searchParams?.get(FilterType.Radius);

        if (radius) {
            this.radiusInputElement.value = radius;
            radiusFilterValueElement.textContent = radius + " km";
        }

        if (this.state.filters.selectedValues[SelectFilterType.Locations].length) {
            radiusFilterContent?.classList.add("active");
        } else {
            radiusFilterContainer?.classList.remove("active");
            radiusFilterContent?.classList.remove("active");
        }

        this.radiusInputElement.addEventListener("input", (event) => {
            if (!(event.target instanceof HTMLInputElement)) return;

            radiusFilterValueElement.textContent = event.target.value + " km";

            this.state.filters[FilterType.Radius] = parseInt(event.target.value, 10);
            this.refreshJobListings();
        });

        radiusFilterHeader?.addEventListener("click", () => {
            radiusFilterContainer?.classList.toggle("active");
        });
    }
}